import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  EMPTY_SCHEDULER_SELECTION,
  SchedulerSelection,
} from '@/components/integration/nylas/scheduler/types'
import { ClientDetailsFormValues } from '@/pages/referralLink/constants'
import { normalizeClientDetailsValues } from '@/pages/referralLink/helpers'
import { RootState } from '@/redux/store'

export type ReferralLinkStep = 'clientDetails' | 'bookMeeting'

export type ReferralLinkSteps = Array<keyof typeof REFERRAL_LINK_STEP>

export interface ReferralLinkState {
  currentStep: ReferralLinkStep
  clientDetails: ClientDetailsFormValues
  /**
   * The format and place chosen on the booking step, kept so they're still
   * there after going back to the client details and forward again.
   */
  schedulerSelection: SchedulerSelection
  hasBookedMeeting: boolean
  isBookingAsClient: boolean
  isFromCompanyBlockedByDpbRules: boolean
}

export const REFERRAL_LINK_STEP: Record<ReferralLinkStep, ReferralLinkStep> = {
  clientDetails: 'clientDetails',
  bookMeeting: 'bookMeeting',
}

export const REFERRAL_LINK_INITIAL_STATE: ReferralLinkState = {
  currentStep: REFERRAL_LINK_STEP.clientDetails,
  clientDetails: {
    email: '',
    telephone: '',
    firstName: '',
    lastName: '',
    services: [],
    circumstances: '',
  },
  schedulerSelection: EMPTY_SCHEDULER_SELECTION,
  hasBookedMeeting: false,
  isBookingAsClient: true,
  isFromCompanyBlockedByDpbRules: false,
}

export const referralLink = createSlice({
  name: 'referralLink',
  initialState: REFERRAL_LINK_INITIAL_STATE,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<ReferralLinkStep>) => {
      state.currentStep = action.payload
    },

    setClientDetails: (
      state,
      action: PayloadAction<ReferralLinkState['clientDetails']>,
    ) => {
      state.clientDetails = normalizeClientDetailsValues(action.payload)
    },

    setSchedulerSelection: (
      state,
      action: PayloadAction<ReferralLinkState['schedulerSelection']>,
    ) => {
      state.schedulerSelection = action.payload
    },

    setHasBookedMeeting: (
      state,
      action: PayloadAction<ReferralLinkState['hasBookedMeeting']>,
    ) => {
      state.hasBookedMeeting = action.payload
    },

    setIsBookingAsClient: (
      state,
      action: PayloadAction<ReferralLinkState['isBookingAsClient']>,
    ) => {
      state.isBookingAsClient = action.payload
    },

    setIsFromCompanyBlockedByDpbRules: (
      state,
      action: PayloadAction<
        ReferralLinkState['isFromCompanyBlockedByDpbRules']
      >,
    ) => {
      state.isFromCompanyBlockedByDpbRules = action.payload
    },
  },
})

export const {
  setCurrentStep,
  setClientDetails,
  setSchedulerSelection,
  setHasBookedMeeting,
  setIsBookingAsClient,
  setIsFromCompanyBlockedByDpbRules,
} = referralLink.actions

export const selectReferralLinkSlice = (state: RootState) => state.referralLink

export const selectCurrentStep = createSelector(
  selectReferralLinkSlice,
  (state) => state.currentStep,
)

export const selectClientDetails = createSelector(
  selectReferralLinkSlice,
  (state) => state.clientDetails,
)

export const selectSchedulerSelection = createSelector(
  selectReferralLinkSlice,
  (state) => state.schedulerSelection,
)

export const selectHasBookedMeeting = createSelector(
  selectReferralLinkSlice,
  (state) => state.hasBookedMeeting,
)

export const selectIsBookingAsClient = createSelector(
  selectReferralLinkSlice,
  (state) => state.isBookingAsClient,
)

export const selectIsFromCompanyBlockedByDpbRules = createSelector(
  selectReferralLinkSlice,
  (state) => state.isFromCompanyBlockedByDpbRules,
)

export const referralLinkReducer = referralLink.reducer
